/**
 * Safe markdown for AI replies (the CFO and Fusion AI): the same small subset CEO Tasks renders (headings, bullet and numbered lists, paragraphs,
 * bold, italic, inline code, http(s) links, and internal links starting with "/"), built as React elements so no model text ever reaches the DOM as HTML.
 */
import { useRouter } from "@tanstack/react-router";
import { Fragment, type MouseEvent, type ReactNode } from "react";

/** Same-tab link to a page inside ONE, moved by the router (modifier clicks still open a new tab). */
function InternalLink({ href, children }: { href: string; children: ReactNode }) {
  const router = useRouter();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    router.history.push(href);
  };
  return <a href={href} onClick={onClick} className="text-brand-600 underline">{children}</a>;
}

// Same rules as CEO Tasks' mdToHtml + linkify; links drop the utm_source=openai tag and open in a new tab
const INLINE: Array<{ re: RegExp; make: (m: RegExpExecArray, key: number) => ReactNode; lead?: number }> = [
  {
    re: /\[([^\]\n<]{1,160})\]\((https?:\/\/[^\s)<"']+)\)/,
    make: (m, k) => <a key={k} href={m[2]!.replace(/[?&]utm_source=openai\b/, "")} target="_blank" rel="noreferrer noopener" className="text-brand-600 underline">{m[1]}</a>,
  },
  // internal paths only: "/x", never "//host" or "/\\host" (both leave the site)
  { re: /\[([^\]\n<]{1,160})\]\((\/(?![/\\])[^\s)<"'\\]*)\)/, make: (m, k) => <InternalLink key={k} href={m[2]!}>{m[1]}</InternalLink> },
  { re: /\*\*(.+?)\*\*/, make: (m, k) => <b key={k}>{inline(m[1]!)}</b> },
  { re: /`([^`\n]+)`/, make: (m, k) => <code key={k} className="rounded bg-[rgb(11_12_42/0.06)] px-1 text-[0.85em]">{m[1]}</code> },
  // the leading space or bracket stays as text
  { re: /(^|[\s(])\*([^*\n]+)\*(?=[\s).,;:!?]|$)/, make: (m, k) => <Fragment key={k}>{m[1]}<i>{inline(m[2]!)}</i></Fragment> },
];

function inline(src: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = src;
  let key = 0;
  while (rest) {
    let best: { m: RegExpExecArray; make: (m: RegExpExecArray, key: number) => ReactNode } | null = null;
    for (const r of INLINE) {
      const m = r.re.exec(rest);
      if (m && (!best || m.index < best.m.index)) best = { m, make: r.make };
    }
    if (!best) { out.push(rest); break; }
    if (best.m.index > 0) out.push(rest.slice(0, best.m.index));
    out.push(best.make(best.m, key++));
    rest = rest.slice(best.m.index + best.m[0].length);
  }
  return out;
}

export function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let list: { kind: "ul" | "ol"; start: number; items: ReactNode[] } | null = null;
  let olSeq = 0; // numbered items keep counting across a list broken up by bullets; a heading restarts it
  const close = () => {
    if (!list) return;
    const k = blocks.length;
    blocks.push(list.kind === "ul"
      ? <ul key={k} className="my-1 list-disc pl-5">{list.items}</ul>
      : <ol key={k} start={list.start} className="my-1 list-decimal pl-5">{list.items}</ol>);
    list = null;
  };
  for (const raw of String(text || "").split(/\r?\n/)) {
    const line = raw.trimEnd();
    const h = /^(#{1,4})\s+(.*)/.exec(line);
    const ul = /^\s*[-*]\s+(.*)/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)/.exec(line);
    if (h) {
      close();
      olSeq = 0;
      const size = h[1]!.length === 1 ? "text-[0.95rem]" : "text-[0.9rem]";
      blocks.push(<p key={blocks.length} className={`mb-0.5 mt-2 font-bold ${size}`}>{inline(h[2]!)}</p>);
    } else if (ul) {
      if (list?.kind !== "ul") { close(); list = { kind: "ul", start: 1, items: [] }; }
      list!.items.push(<li key={list!.items.length}>{inline(ul[1]!)}</li>);
    } else if (ol) {
      if (list?.kind !== "ol") { close(); list = { kind: "ol", start: olSeq + 1, items: [] }; }
      olSeq += 1;
      list!.items.push(<li key={list!.items.length}>{inline(ol[1]!)}</li>);
    } else if (!line.trim()) {
      close();
      blocks.push(<div key={blocks.length} className="h-1.5" />);
    } else {
      close();
      blocks.push(<p key={blocks.length}>{inline(line)}</p>);
    }
  }
  close();
  return <div className="break-words">{blocks}</div>;
}
